import React, { useEffect, useState } from "react";
import { Modal, message, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { useModalStore } from "@/hooks/useModalStore";
import { usePaymentStore } from "@/hooks/usePaymentStore";
import { payPlaceTypeLists } from "@/components/config";
import { s3ImageLink } from "@/utils/links";
import { useTranslation } from "react-i18next";
import { isMobile } from "@/biz/tool";
import TopupPaymentStatusModal, {
  TopupPaymentStatus,
} from "./TopupPaymentStatusModal";
interface PlanModalProps {}

const ModalUpdatePlan: React.FC<PlanModalProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    showRechargeResultModal,
    setShowRechargeResultModal,
    rechargeResultModalContent,
    setRechargeResultModalContent,
    setPayPlace,
    setChargeModalInfo,
  } = useModalStore();
  const { sendCheckPaymentRetrieve, reQueryWalletData } = usePaymentStore();
  const [stripeHppStatus, setStripeHppStatus] =
    useState<TopupPaymentStatus | null>(null);
  const [stripeHppAmount, setStripeHppAmount] = useState<number | null>(null);

  const getHashParams = () => {
    const hashSearch = window.location.hash.split("?")[1] || "";
    const hashParams = new URLSearchParams(hashSearch);
    const searchParams = new URLSearchParams(window.location.search);
    const getParam = (key: string) =>
      hashParams.get(key) ?? searchParams.get(key);

    return {
      type: getParam("type"),
      from: getParam("from"),
      result: getParam("result"),
      sessionId: getParam("session_id"),
      amount: getParam("amount"),
      payPlace: getParam("payPlace"),
      campaignId: getParam("campaignId"),
    };
  };
  const [nowPayPlace, setNowPayPlace] = useState<string | null | undefined>("");

  useEffect(() => {
    const { type, from, result, sessionId, amount, payPlace, campaignId } =
      getHashParams();
    // HPP 成功/失败以支付服务端回跳的 result 为准：
    // success 表示成功，cancelled（及其他非 success 值）表示失败。
    // Stripe 使用 type=checkout，Airwallex 使用 from=awx 标记回跳来源。
    const isHppCallback = Boolean(result) && (type === "checkout" || from === "awx");
    const isSuccessful = result === "success";

    if (isHppCallback) {
      const callbackAmount = amount === null ? Number.NaN : Number(amount);
      setStripeHppAmount(
        Number.isFinite(callbackAmount) ? callbackAmount : null,
      );
      const isSpecialSuccessFlow =
        isSuccessful &&
        [
          payPlaceTypeLists.ORDER_LIST,
          payPlaceTypeLists.CAMPAIGN_CREATE_RELEASE,
          payPlaceTypeLists.CAMPAIGN_CREATE_RELEASE_ACTIVE,
        ].includes(payPlace as any);

      setNowPayPlace(payPlace);
      if (isSpecialSuccessFlow) {
        setRechargeResultModalContent(
          t("components.modalRechargeResult.checking"),
        );
        setShowRechargeResultModal(true);
      } else {
        // Stripe 与 Airwallex HPP 共用结果展示样式，结果仍由回跳参数决定。
        setStripeHppStatus("processing");
      }

      const showResult = () => {
        if (!isSpecialSuccessFlow) {
          setStripeHppStatus(isSuccessful ? "success" : "failed");
        } else {
          setRechargeResultModalContent(
            t("components.modalRechargeResult.accountCreditedMessage", {
              amount: Number(amount || 0).toLocaleString("en-US"),
            }),
          );
        }
      };

      if (sessionId) {
        // 保留 Stripe 原有的核验与余额刷新；展示状态不再依赖该请求的响应体。
        sendCheckPaymentRetrieve({ sessionId }).catch((error) => {
          console.error("Payment verification error:", error);
        });
      } else if (isSuccessful) {
        // Airwallex HPP 没有 Stripe session_id，成功回跳后仅刷新余额。
        void reQueryWalletData({ andLoading: false });
      }

      window.setTimeout(showResult, 2000);
    }
    if (type === "subscription") {
      if (result === "success") {
        message.success(
          t("components.modalRechargeResult.subscriptionSuccess"),
        );
      } else {
        message.error(
          t("components.modalRechargeResult.subscriptionFailedTryAgain"),
        );
      }
    }
    if (isHppCallback || type === "subscription") {
      // 清除URL参数
      const cleanHash = window.location.hash.split("?")[0];
      window.history.replaceState(
        {},
        "",
        window.location.pathname +
          (payPlace === payPlaceTypeLists.CAMPAIGN_CREATE_RELEASE ||
          payPlace === payPlaceTypeLists.CAMPAIGN_CREATE_RELEASE_ACTIVE
            ? `?campaignId=${campaignId}`
            : cleanHash),
      );
    }
  }, []);

  const handleClose = (isOk?: boolean) => {
    if (
      nowPayPlace === payPlaceTypeLists.CAMPAIGN_CREATE_RELEASE ||
      nowPayPlace === payPlaceTypeLists.CAMPAIGN_CREATE_RELEASE_ACTIVE
    ) {
      if (
        rechargeResultModalContent ===
        t("components.modalRechargeResult.checking")
      ) {
        return;
      }
      // 在当前url基础上添加deposit=1的query参数
      const url = new URL(window.location.href);
      url.searchParams.set("deposit", "1");
      // 使用React Router的navigate来确保URL变化能被正确检测到
      navigate(url.pathname + url.search + url.hash, { replace: true });
    }
    setShowRechargeResultModal(false);
    setChargeModalInfo({
      amount: null,
    });
    setPayPlace("");
    if (isOk) {
      navigate("/organization#/payment");
    }
  };

  const handleStripeHppStatusClose = () => {
    setStripeHppStatus(null);
    setStripeHppAmount(null);
    setShowRechargeResultModal(false);
    setRechargeResultModalContent("");
    setChargeModalInfo({ amount: null });
    setPayPlace("");
  };

  if (stripeHppStatus) {
    return (
      <TopupPaymentStatusModal
        status={stripeHppStatus}
        amount={stripeHppAmount}
        onBack={handleStripeHppStatusClose}
      />
    );
  }

  if (nowPayPlace === payPlaceTypeLists.ORDER_LIST) {
    return (
      <Modal
        width={564}
        open={showRechargeResultModal}
        closable={false}
        footer={null}
        styles={{
          content: {
            borderRadius: "24px",
            padding: "32px",
          },
        }}
        onCancel={() => {
          setShowRechargeResultModal(false);
        }}
      >
        <div className="w-full">
          <img
            src={`${s3ImageLink}recharge-successful.png`}
            className=" w-[257px] h-[128px] m-auto mb-6 block"
          />
          <div className="text-center justify-start text-text-color-primary text-xl font-semibold  leading-loose">
            {t("components.modalRechargeResult.youreAllSet")}
          </div>
          <div className="self-stretch text-center justify-start">
            <span className="text-text-color-secondary text-base font-normal  leading-normal">
              {t("components.modalRechargeResult.paymentMethodConfirmed")}
            </span>
            <span className="text-text-color-primary text-base font-semibold  leading-normal">
              {t("components.modalRechargeResult.fullInfluencerOutreach")}
            </span>
            <span className="text-text-color-secondary text-base font-normal  leading-normal">
              {t("components.modalRechargeResult.forYourCampaign")}
            </span>
          </div>
          <Button
            className="w-full mt-8"
            type="primary"
            size={"large"}
            onClick={() => {
              setPayPlace("");
              setShowRechargeResultModal(false);
            }}
          >
            {t("components.modalRechargeResult.gotIt")}
          </Button>
        </div>
      </Modal>
    );
  }
  if (
    nowPayPlace === payPlaceTypeLists.CAMPAIGN_CREATE_RELEASE ||
    nowPayPlace === payPlaceTypeLists.CAMPAIGN_CREATE_RELEASE_ACTIVE
  ) {
    return (
      <Modal
        width={624}
        open={showRechargeResultModal}
        footer={null}
        styles={{
          content: {
            borderRadius: "24px",
            padding: "32px",
          },
        }}
        onCancel={() => handleClose(false)}
      >
        <div className="w-full">
          <img
            src={`${s3ImageLink}recharge-successful.png`}
            className=" w-[257px] h-[128px] m-auto mb-6 block"
          />
          <div className="w-[458.35px] mx-auto mt-6 text-center justify-start text-text-color-primary text-2xl font-semibold font-['DM_Sans'] leading-loose">
            {t("components.modalRechargeResult.campaignDepositSuccessful")}
          </div>
          <div className="self-stretch mt-3 text-center justify-start text-font&icon-secondary text-sm font-normal font-['DM_Sans'] leading-snug">
            {nowPayPlace === payPlaceTypeLists.CAMPAIGN_CREATE_RELEASE_ACTIVE
              ? t("components.modalRechargeResult.campaignAlmostReadyActive")
              : t("components.modalRechargeResult.campaignAlmostReady")}
          </div>
          <Button
            className="w-full mt-8"
            type="primary"
            size={"large"}
            onClick={() => handleClose(false)}
            disabled={
              rechargeResultModalContent ===
              t("components.modalRechargeResult.checking")
            }
          >
            {nowPayPlace === payPlaceTypeLists.CAMPAIGN_CREATE_RELEASE_ACTIVE
              ? t("components.modalRechargeResult.updateCampaign")
              : t("components.modalRechargeResult.completeSetup")}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={t("components.modalRechargeResult.topUpComplete")}
      open={showRechargeResultModal}
      onCancel={() => handleClose(false)}
      onOk={() => handleClose(true)}
      okText={t("components.modalRechargeResult.gotIt")}
      footer={isMobile() ? null : (_, { OkBtn }) => <OkBtn />}
    >
      <div>{rechargeResultModalContent}</div>
    </Modal>
  );
};

export default ModalUpdatePlan;
